// FILE: src/features/dashboardSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import fetchWithAuth from "../utils/fetchWithAuth";

// GET /api/admin/dashboard/stats/enrollment-stats (WP-7) →
// { totalStudents, invited, registered, notStarted, inProgress, completed,
//   surveyPending, locked, expiring, enrolmentEnded, untimed, chapters, homelinks }
// (untimed reviewer accounts are left out of every figure; `untimed` counts them)
export const getEnrollmentStats = createAsyncThunk(
  "dashboard/getEnrollmentStats",
  async (_, { rejectWithValue }) => {
    try {
      const url = "/api/admin/dashboard/stats/enrollment-stats";
      const data = await fetchWithAuth(url);
      return data;
    } catch (err) {
      return rejectWithValue(err.message || "Failed to fetch enrollment stats");
    }
  }
);

/** "?from=…&to=…" for optional Toronto days (YYYY-MM-DD, `to` included); "" for all time. */
export function rangeQuery({ from, to } = {}) {
  const params = new URLSearchParams();
  if (from) params.set("from", from);
  if (to) params.set("to", to);
  const q = params.toString();
  return q ? `?${q}` : "";
}

// GET /api/admin/dashboard/stats/time-stats ?from&to (WP-7) →
// { from, to, totalInClassHours, totalHomeLinkHours, totalUnattributedHours,
//   totalHours, avgDailyUsage, activeStudentDays, studentsWithTime,
//   exceededDailyLimitCount, cappedDays }
export const getTimeStats = createAsyncThunk(
  "dashboard/getTimeStats",
  async (range = {}, { rejectWithValue }) => {
    try {
      return await fetchWithAuth(`/api/admin/dashboard/stats/time-stats${rangeQuery(range || {})}`);
    } catch (err) {
      return rejectWithValue(err.message || "Failed to fetch time stats");
    }
  }
);

// GET /api/admin/dashboard/stats/quiz-stats ?from&to (WP-7) →
// { totalAttempts, students, passed, failed, noPassMark, averageScore,
//   byResource: [{ resourceId, title, resourceType, order, attempts, students,
//   averageScore, latestAverageScore, passMark, passed, failed }],
//   mostMissedQuestions: [], questionDataAvailable: false }
export const getQuizStats = createAsyncThunk(
  "dashboard/getQuizStats",
  async (range = {}, { rejectWithValue }) => {
    try {
      return await fetchWithAuth(`/api/admin/dashboard/stats/quiz-stats${rangeQuery(range || {})}`);
    } catch (err) {
      return rejectWithValue(err.message || "Failed to fetch quiz stats");
    }
  }
);

// calls GET /api/admin/dashboard/stats/support-feedback-stats
export const getSupportFeedbackStats = createAsyncThunk(
  "dashboard/getSupportFeedbackStats",
  async (_, { rejectWithValue }) => {
    try {
      const url = "/api/admin/dashboard/stats/support-feedback-stats";
      const data = await fetchWithAuth(url);
      // data might look like:
      // {
      //   tickets: { open: 10, resolved: 45, avgResolutionHours: 12.3 },
      //   feedback: { submissionsLast30Days: 20, averageRating: 4.2 }
      // }
      return data;
    } catch (err) {
      return rejectWithValue(
        err.message || "Failed to fetch support/feedback stats"
      );
    }
  }
);

// GET /api/admin/dashboard/trends/monthly-trends?period=3months|6months|1year|2years (WP-7) →
// { period, from, to, timeZone, studentsPerMonth, invitesPerMonth, completionsPerMonth:
//   [{ year, month, count }], timeUsagePerMonth: [{ year, month, inClassHours,
//   homeLinkHours, totalHours, …Minutes }] } (every month of the period, zeros included)
export const getMonthlyTrends = createAsyncThunk(
  "dashboard/getMonthlyTrends",
  async (period, { rejectWithValue }) => {
    try {
      return await fetchWithAuth(`/api/admin/dashboard/trends/monthly-trends?period=${encodeURIComponent(period)}`);
    } catch (err) {
      return rejectWithValue(err.message || "Failed to fetch monthly trends");
    }
  }
);

// Each figure group loads on its own (B-14): `status[key]` is
// "idle" | "loading" | "succeeded" | "failed" and `errors[key]` keeps the
// failure for <ErrorState>, so one slow or failed request never blanks the
// whole dashboard. `loading` / `error` stay for older callers.
const SECTIONS = [
  [getEnrollmentStats, "enrollment", "enrollmentStats"],
  [getTimeStats, "time", "timeStats"],
  [getQuizStats, "quiz", "quizStats"],
  [getSupportFeedbackStats, "supportFeedback", "supportFeedbackStats"],
  [getMonthlyTrends, "trends", "monthlyTrends"]
];

const anyLoading = (state) => Object.values(state.status).some((s) => s === "loading");

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState: {
    supportFeedbackStats: null,
    monthlyTrends: null,
    quizStats: null,
    enrollmentStats: null,
    timeStats: null,
    status: { enrollment: "idle", time: "idle", quiz: "idle", supportFeedback: "idle", trends: "idle" },
    errors: { enrollment: null, time: null, quiz: null, supportFeedback: null, trends: null },
    loading: false,
    error: null
  },
  reducers: {},
  extraReducers: (builder) => {
    SECTIONS.forEach(([thunk, key, field]) => {
      builder
        .addCase(thunk.pending, (state) => {
          state.status[key] = "loading";
          state.errors[key] = null;
          state.loading = true;
          state.error = null;
        })
        .addCase(thunk.fulfilled, (state, action) => {
          state.status[key] = "succeeded";
          state[field] = action.payload;
          state.loading = anyLoading(state);
        })
        .addCase(thunk.rejected, (state, action) => {
          state.status[key] = "failed";
          state.errors[key] = action.payload || action.error?.message || null;
          state.loading = anyLoading(state);
          state.error = state.errors[key];
        });
    });
  }
});

export default dashboardSlice.reducer;
